import styled, { css } from 'styled-components';

import Link from '../../UI/Link/Link';
import variables from '../../../styles/variables';
import Image from '../../UI/Image/Image';

interface Props {
  locale: string;
  count: number;
}

export const Container = styled.header`
  background: ${variables.colorWhite};
  flex-direction: row;
  position: relative;
  z-index: 999;
  width: 100%;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);

  @media screen and (max-width: 1023px) {
    height: auto;
    min-height: 45px;
  }

  &.is-stuck {
    box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.15);
    transition: box-shadow 50ms ease-out;
  }
`;

export const DesktopWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const InnerContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: ${variables.defaultWidth}px;
  display: flex;
  &.locale {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    padding: 10px 2.5rem;
    @media screen and (max-width: 1023px) {
      padding: 10px 1.25rem;
    }
  }
  &.navigation {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 2.5rem;
    @media screen and (max-width: 1023px) {
      padding: 10px 1.25rem;
    }
  }
`;

export const HamburgerButton = styled.button`
  position: absolute;
  right: 20px;
  background-color: transparent;
  padding: 0;
  border: 0;
  cursor: pointer;
  div.hamburgerBar {
    width: 24px;
    height: 3px;
    background: rgb(51, 51, 51);
    margin: 4px;
  }
`;

export const MobileWrapper = styled.div`
  margin: 0 auto;
  max-width: ${variables.defaultWidth}px;
  padding: 0.5rem 1.25rem;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  display: flex;
`;

export const MainNav = styled.div`
  width: 65%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

export const LogoContainer = styled.div<Props>`
  display: flex;
  flex-shrink: 0;
  width: ${({ locale }) => (locale === 'fr' ? '230px' : '210px')};
  margin-right: 4%;
  @media screen and (max-width: 1280px) {
    width: 180px;
    max-width: none;
  }
  @media screen and (max-width: 1024px) {
    margin: 0;
  }
  @media screen and (max-width: 380px) {
    width: 130px;
  }
  img {
    height: 55px;
    @media screen and (max-width: 1024px) {
      height: 48px;
    }
  }
`;

export const StyledImage = styled(Image)`
  margin: 0;
`;

export const Navigation = styled.nav<Props>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  nav {
    display: flex;
    align-items: center;
    position: relative;
  }
  ul {
    margin: 0 3rem 0 0;
    list-style-type: none;
    display: flex;
    padding: 0;
    justify-content: space-between;
    min-width: 65%;

    @media screen and (max-width: 1280px) {
      min-width: 60%;
    }

    ${({ count }: Props) => (count === 5
        && css`
          width: 90%;
        `)
      || (count === 6
        && css`
          width: 80%;
        `)}
  }
  li {
    margin: 0;
    padding: 0;
    text-align: center;

    /* Currently TextLink takes richtext so we have to allow for any tag coming down */
    * {
      margin: 0;
      text-decoration: none;
      cursor: pointer;
      font-size: ${({ locale }) => (locale === 'fr' ? '16px' : '20px')};
      line-height: 26px;
      font-weight: 600 !important;
      letter-spacing: 1.14px !important;
      display: table;
      position: relative;

      ${({ count, locale }: Props) => count === 6
        && css`
          font-size: ${locale === 'fr' ? '13px' : '14px'};

          @media screen and (max-width: 1280px) {
            font-size: ${locale === 'fr' ? '12px' : '14px'};
          }

          &:last-child {
            * {
              color: ${variables.colorRed};
            }
          }
        `}

      @media screen and (max-width: 1280px) {
        font-size: ${({ locale }) => (locale === 'fr' ? '12px' : '14px')};
      }

      &:hover {
        text-decoration: underline;
      }

      .overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        z-index: 20;
      }
    }

    &:last-child {
      margin-right: 0;
      a {
        ${({ count }: Props) => count === 7
          && css`
            color: ${variables.colorRed};
          `}
      }
    }

    &.why-choose-us {
      @media screen and (max-width: 1280px) {
        display: none;
      }
    }
  }
`;

export const NavLink = styled(Link)`
  margin: 0;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.14px;
  display: table;
  position: relative;
  &:hover {
    text-decoration: underline;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    z-index: 20;
  }
`;

export const LanguagePickerWrapper = styled.div`
  width: 100%;
  background-color: #f4f4f4;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  a {
    text-decoration: none;
  }
  a.active {
    text-decoration: none;
    pointer-events: none;
    opacity: 0.5;
  }
`;

export const SkipContainer = styled.div`
  margin: 0 10px;
  position: relative;
`;

export const SkipNavLink = styled.button`
  font-size: 11px;
  font-weight: 400;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin: 0;
  clip: rect(0px, 0px, 0px, 0px);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  z-index: -999;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: 5px auto Highlight;
    outline: 5px auto -webkit-focus-ring-color;
    border-radius: 4px;
    left: auto;
    top: auto;
    position: relative;
    clip: none;
    clip-path: none;
    width: auto;
    height: auto;
    overflow: auto;
    z-index: 999;
  }
  span {
    padding-right: 10px;
  }
`;

export const StyledListItem = styled.li<{color?: string}>`
  a {
    ${({ color }) => color && `color: ${color}`};
  }
`;

export const SSRHeaderContainer = styled.div`
  width: 100%;
  height: 116.8px;
  @media screen and (max-width: 767px) {
    height: 67.8px;
  }
`;

export const SSRLogoContainer = styled.div`
  padding: 10px 2.5rem;
  @media screen and (max-width: 767px) {
    padding: 0.5rem 1.25rem;
  }
`;

export const SSRLanguagePicker = styled.div`
  width: 100%;
  background-color: #f4f4f4;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  a {
    text-decoration: none;
  }
  a.active {
    text-decoration: none;
    pointer-events: none;
    opacity: 0.5;
  }
  @media screen and (max-width: 767px) {
    display: none;
  }
`;
